<template>
    <Button
        v-if="failedSyncs.length > 0"
        icon="pi pi-refresh"
        :severity="failedSyncs.length > 0 ? 'danger' : 'secondary'"
        label=""
        @click="toggleOp"
        aria-label="Sync Status"
    />
    <Popover ref="op">
        <div class="popover-container">
            <div class="popover-header">
                <h3>Sync Status</h3>
                <p class="popover-description">
                    This shows entities that failed to sync with the server. You
                    need to take action to resolve these issues.
                </p>
            </div>
            <DataTable
                v-if="failedSyncs.length > 0"
                :value="failedSyncs"
                tableStyle="min-width: 50rem"
            >
                <Column field="fake" header="">
                    <template #body>
                        <Badge
                            severity="danger"
                            value="Not synced"
                            class="sync-status-badge"
                        />
                    </template>
                </Column>
                <Column field="identity.entityType" header="Type"></Column>
                <Column field="name" header="Entity Name">
                    <template #body="slotProps">
                        <EntityName
                            :entity-type="slotProps.data.identity.entityType"
                            :entity-id="slotProps.data.identity.entityId"
                        />
                    </template>
                </Column>
                <Column field="actions" header="Actions">
                    <template #body="slotProps">
                        <div class="sync-action-buttons">
                            <Button
                                severity="secondary"
                                label="View"
                                class="view-button"
                                aria-label="View"
                                as="RouterLink"
                                :to="{
                                    name: slotProps.data.page.name,
                                    params: slotProps.data.page.params,
                                }"
                                v-tooltip.top="
                                    'View and edit this entity to fix sync issues'
                                "
                            />
                            <Button
                                icon="pi pi-download"
                                severity="secondary"
                                label="Download"
                                @click="slotProps.data.actions.download()"
                                aria-label="Download"
                                v-tooltip.top="
                                    'Download a copy of your unsaved changes'
                                "
                            />
                        </div>
                    </template>
                </Column>
                <Column field="details" header="Details">
                    <template #body="slotProps">
                        {{ slotProps.data.details || "Unknown error" }}
                    </template>
                </Column>
            </DataTable>
            <div class="info-text">
                <template v-if="failedSyncs.length > 1">
                    <strong>Manual actions are required.</strong> You need to
                    resolve these sync issues by viewing each entity and fixing
                    the conflicts.
                </template>
                <template v-else-if="failedSyncs.length > 0">
                    <strong>Manual action is required.</strong> You need to
                    resolve this sync issue by viewing the entity and fixing the
                    conflict.
                </template>
                <template v-else-if="isBeingSynced"
                    >Syncing changes to the server...</template
                >
                <template v-else-if="!isInSync">
                    There are changes that need to be synced to the server.
                    Please wait for the sync to complete.
                </template>
                <template v-else-if="isInSync">
                    Everything is up to date. All changes have been successfully
                    synced to the server.
                </template>
            </div>
        </div>
    </Popover>
</template>

<script lang="ts" setup>
import EntityName from "@/components/SyncStatusEntityName.vue";
import { useSyncManager } from "@/stores/sync-store";
import Popover from "primevue/popover";
import { ref } from "vue";

const op = ref<InstanceType<typeof Popover>>();
const toggleOp = (event: Event) => {
    op.value?.toggle(event);
};
const { failedSyncs, isBeingSynced, isInSync } = useSyncManager();
</script>

<style lang="scss" scoped>
.view-button {
    text-decoration: none;
}

.info-text {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: var(--text-color);
}

.popover-container {
    padding: 0.5rem;
    max-width: 60rem;
}

.popover-header {
    margin-bottom: 1rem;

    h3 {
        margin: 0 0 0.5rem 0;
    }

    .popover-description {
        margin: 0;
        font-size: 0.875rem;
        color: var(--text-color-secondary);
    }
}

.sync-action-buttons {
    display: flex;
    gap: 0.5rem;
}

.sync-status-badge {
    min-width: 6rem;
}
</style>
